import {
  BargeControlBilgePumpEndpoints,
  BargeControlPlcEndpoints,
  BargeControlSiloEndpoints,
  BargeControlTankEndpoints,
  IBargeControlInfo,
  InventoryType,
  ISiloInfo,
  IUpdateBilgePumpRequest,
} from '@scaleaq/sitecontrol-lib';

import { API_NAMES, CACHE_KEYS } from '@/constants';
import {
  type ServiceQueryOptions,
  useAuthApiDelete,
  useAuthApiGetMultiFetch,
  useAuthApiGetSingleFetch,
  useAuthApiPost,
  useAuthApiUpdate,
} from '@/dataAccess/rest/apiHooks';

import type {
  BargeControlDeviceType,
  SetDieselDetectionCountParams,
} from './bargeControl.types.ts';

const plcUrlSegment = BargeControlPlcEndpoints.addPlc;
const bilgePumpUrlSegment = BargeControlBilgePumpEndpoints.getAllActivatedBilgePumps;
const tankUrlSegment = BargeControlTankEndpoints.getAllActivatedTanks;
const siloUrlSegment = BargeControlSiloEndpoints.addSilo;

const getRouteSegment = (deviceType?: InventoryType) => {
  switch (deviceType) {
    case InventoryType.BargeControlPlc:
      return plcUrlSegment;
    case InventoryType.BilgePump:
      return bilgePumpUrlSegment;
    case InventoryType.BargeControlTank:
      return tankUrlSegment;
    case InventoryType.BargeControlSilo:
      return siloUrlSegment;
  }
  return '';
};

export const useGetAllBargeControlDevices = ({ enabled }: ServiceQueryOptions = {}) => {
  return useAuthApiGetMultiFetch<BargeControlDeviceType>({
    baseApiRoute: API_NAMES.bargecontrolApi,
    cacheKeys: [CACHE_KEYS.allBargeControlDevices],
    routesArray: [plcUrlSegment, bilgePumpUrlSegment, tankUrlSegment, siloUrlSegment],
    enabled,
  });
};

export const useGetAllBargeControlPLCs = ({ enabled }: ServiceQueryOptions = {}) => {
  return useAuthApiGetSingleFetch<IBargeControlInfo[]>({
    baseApiRoute: API_NAMES.bargecontrolApi,
    cacheKeys: [CACHE_KEYS.bargeControlPlcs],
    route: BargeControlPlcEndpoints.getPlcs,
    enabled,
  });
};

export const useAddBargeControlDevice = (deviceType?: InventoryType, identifier?: string) => {
  return useAuthApiPost({
    baseApiRoute: API_NAMES.bargecontrolApi,
    route: getRouteSegment(deviceType),
    cacheKeys: [CACHE_KEYS.allBargeControlDevices, CACHE_KEYS.bargeControlPlcs],
    identifier: identifier,
  });
};

export const useUpdateBargeControlTank = (device: BargeControlDeviceType | null | undefined) => {
  return useAuthApiUpdate({
    baseApiRoute: API_NAMES.bargecontrolApi,
    route: getRouteSegment(InventoryType.BargeControlTank),
    id: device?.id ?? '',
    cacheKeys: [CACHE_KEYS.allBargeControlDevices, CACHE_KEYS.initialSensorData],
    identifier: device?.name,
    putOrPatch: 'PATCH',
  });
};
export const useUpdateBargeControlBilgePump = (id: string, name: string) => {
  return useAuthApiUpdate<IUpdateBilgePumpRequest>({
    baseApiRoute: API_NAMES.bargecontrolApi,
    route: getRouteSegment(InventoryType.BilgePump),
    id: id,
    cacheKeys: [CACHE_KEYS.allBargeControlDevices, CACHE_KEYS.initialSensorData],
    identifier: name,
    putOrPatch: 'PATCH',
  });
};

export const useUpdateBargeControlSilo = (device: BargeControlDeviceType | null | undefined) => {
  return useAuthApiUpdate({
    baseApiRoute: API_NAMES.bargecontrolApi,
    route: getRouteSegment(InventoryType.BargeControlSilo),
    id: (device as ISiloInfo)?.id?.toString() ?? '',
    cacheKeys: [CACHE_KEYS.allBargeControlDevices, CACHE_KEYS.initialSensorData],
    identifier: device?.name,
    putOrPatch: 'PATCH',
  });
};

/** The PLC command takes both values in the query string and no body. */
const buildDieselDetectionCountRoute = ({
  dieselDetectionCount,
  inventoryId,
}: Partial<SetDieselDetectionCountParams>): string => {
  const params = new URLSearchParams({
    inventoryId: inventoryId ?? '',
    dieselDetectionCount: String(dieselDetectionCount ?? ''),
  });
  return `${BargeControlPlcEndpoints.setDieselDetectionCount}?${params.toString()}`;
};

export const useUpdateDieselDetectionCount = (identifier?: string) => {
  return useAuthApiPost<SetDieselDetectionCountParams>({
    baseApiRoute: API_NAMES.bargecontrolApi,
    cacheKeys: [CACHE_KEYS.allBargeControlDevices, CACHE_KEYS.initialSensorData],
    identifier,
    resolveRoute: buildDieselDetectionCountRoute,
    route: BargeControlPlcEndpoints.setDieselDetectionCount,
    sendEmptyBody: true,
  });
};

export const useRemoveBargeControlDevice = (device: BargeControlDeviceType | null) => {
  return useAuthApiDelete({
    baseApiRoute: API_NAMES.bargecontrolApi,
    cacheKeys: [CACHE_KEYS.allBargeControlDevices, CACHE_KEYS.bargeControlPlcs],
    id: device?.id ?? '',
    identifier: device?.name,
    route: getRouteSegment(device?.type),
  });
};
